import { useMemo } from "react";
import type { ConnectProto } from "@racoon/api";
import { useGovernmentTrucksQuery, useTrucksQuery } from "~/shared/api/govDash/govDash.api";

type GovTruckInfo = ConnectProto.GovTruckInfo;

const TRUCKS_POLLING_INTERVAL_MS = 10000;

interface UseMapTrucksArgs {
  districtOrganizationIds: bigint[];
  serviceProviderIds: bigint[];
  showMvoTrucks: boolean;
  showGovernmentTrucks: boolean;
}

interface UseMapTrucksResult {
  trucks: GovTruckInfo[];
  /** Ids (as strings) of the trucks that came from GovernmentTrucks. */
  governmentTruckIds: Set<string>;
}

/**
 * Live truck positions for the map: the MVO trucks in the current district/MVO
 * filter plus the government-owned trucks. GovernmentTrucks is outside the MVO
 * scope (not geo-scoped, takes no filter), so those trucks stay on the map
 * whatever districts/MVOs are selected. Each list is fetched only while its
 * option of the trucks filter is on, and polled, which is what moves the markers.
 */
export const useMapTrucks = ({
  districtOrganizationIds,
  serviceProviderIds,
  showMvoTrucks,
  showGovernmentTrucks,
}: UseMapTrucksArgs): UseMapTrucksResult => {
  const pollingOptions = (enabled: boolean) => ({
    pollingInterval: enabled ? TRUCKS_POLLING_INTERVAL_MS : 0,
    skip: !enabled,
    skipPollingIfUnfocused: true,
  });

  // RTK Query keeps the last data of a query after it becomes skipped, so the
  // responses are dropped here explicitly when their layer is switched off.
  const { data: trucksResp } = useTrucksQuery(
    { districtOrganizationIds, serviceProviderIds },
    pollingOptions(showMvoTrucks),
  );
  const { data: governmentTrucksResp } = useGovernmentTrucksQuery(
    {},
    pollingOptions(showGovernmentTrucks),
  );

  return useMemo(() => {
    // Keyed by truck id so a truck returned by both RPCs is drawn once; the
    // government entry wins, so such a truck gets the government icon.
    const byId = new Map<string, GovTruckInfo>();
    const governmentTruckIds = new Set<string>();
    const mvoTrucks = showMvoTrucks ? (trucksResp?.trucks ?? []) : [];
    const governmentTrucks = showGovernmentTrucks ? (governmentTrucksResp?.trucks ?? []) : [];
    for (const info of mvoTrucks) {
      const id = info.truck?.id;
      if (id == null) continue;
      byId.set(String(id), info);
    }
    for (const info of governmentTrucks) {
      const id = info.truck?.id;
      if (id == null) continue;
      byId.set(String(id), info);
      governmentTruckIds.add(String(id));
    }
    return { trucks: Array.from(byId.values()), governmentTruckIds };
  }, [trucksResp, governmentTrucksResp, showMvoTrucks, showGovernmentTrucks]);
};
